import { useState, useRef, useEffect, useContext } from "react";
import { useSession } from "next-auth/client";
import {
  Text, Center, Box, Button, useColorModeValue,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { getAccount } from "utils/sessionHelper";
import { isTweetFromAccount } from "controllers/schedule";
import { TweetContext } from "context/tweetContext";
import { TweetBar } from "./tweet-bar";
import { CsvUpload } from "components/csv-upload";
import { firebaseClient, getToken } from "firebaseClient";
import { CSVLink } from "react-csv";


interface Props {
  isSelected: boolean;
  isOrgDrafts?: boolean;
}

export function DraftsPanel({ isSelected, isOrgDrafts = false }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [cursors, setCursors] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const isAuthenticated = useFirebaseUser();
  const tweetContext: any = useContext(TweetContext);
  const [session] = useSession();
  const tweetbarBg = useColorModeValue("blue.50", "blue.900");
  const threadBg = useColorModeValue("purple.100", "purple.700");
  const csvLink = useRef(null);

  const { isOpen: isOpenUpload, onOpen: onOpenUpload, onClose: onCloseUpload } = useDisclosure();

  useEffect(() => {
    if (isAuthenticated && isSelected && !hasFetched) {
      setHasFetched(true);
      loadDrafts();
    } else {
      getDrafts();
    }
  }, [isAuthenticated, tweetContext.refresh, isSelected]);

  const loadDrafts = async () => {
    setIsLoading(true);
    await getDrafts();
    setIsLoading(false);
  }

  const getDrafts = async (type?: "loadMore" | "all") => {
    if (!isAuthenticated) {
      return;
    }

    const LOAD_BATCH = 30;
    let draftsCopy = type === "loadMore" ? [...drafts] : [];

    if (isOrgDrafts) {

      const response = await fetch('/api/getOrganizationDrafts?batch=10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tokenUserId: session?.user?.uid,
          token: await getToken(session, "getDrafts"),
          cursors,
        }),
      });
      const data = await response.json();

      if (data?.tweets?.length) {
        draftsCopy = [...draftsCopy, ...data.tweets];
        setDrafts(draftsCopy);

        if (data?.cursors?.length)
          setCursors(data?.cursors);
      }

      setHasMore(true);
    }
    else {

      const db = firebaseClient.firestore();

      let query = db
        .collection("users")
        .doc(getAccount(session).id)
        .collection("tweets")
        .where("status", "==", "draft")
        .where("idAccount", "==", getAccount(session).idAccount)
        .orderBy("dateCreated", "desc");

      if (type === "loadMore" && draftsCopy?.length > 0) {
        query = query.startAfter(draftsCopy[draftsCopy?.length - 1]?.dateCreated);
      }

      if (type !== "all")
        query = query.limit(LOAD_BATCH);

      const docs = await query.get();

      docs.forEach((doc) => {
        const data = doc.data();

        if (isTweetFromAccount(data, session)) {
          if (data.text === "") {
            db.collection("users")
              .doc(getAccount(session).id)
              .collection("tweets")
              .doc(doc.id)
              .delete();
          } else {
            draftsCopy.push(data);
          }
        }
      });

      setDrafts(draftsCopy);

      if (docs?.docs?.length === LOAD_BATCH) {
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    }
  };

  const onDelete = async (docId: string) => {
    const filteredDrafts = drafts.filter((d) => d.id !== docId);
    setDrafts(filteredDrafts);
  };

  const loadMoreDrafts = async (isLoadAll = false) => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoadingMore(true);
    await getDrafts(isLoadAll ? "all" : "loadMore");
    setIsLoadingMore(false);
  }

  if (isLoading) {
    return (
      <Center mt="40">
        <PuffLoader
          color={"hsl(203, 100%, 36%)"}
          loading={isLoading}
          css="display: block; margin: 0 auto; border-color: red;"
          size={50}
        />
      </Center>
    );
  }

  const headers = [{ label: "Text", key: "text" }];

  return (
    <Box>
      <Flex w="100%" justifyContent="space-between" alignItems="center">
        <Text pb={4} mt="7" fontSize="lg" fontWeight="700">
          {isOrgDrafts ? "Drafts from my team" : "My Drafts"}
        </Text>
        <Flex>
          <CsvUpload />
          {drafts?.length > 0 && (
            <CSVLink
              data={drafts}
              headers={headers}
              filename={"tweethunter_drafts.csv"}
              className="hidden"
              ref={csvLink}
              target="_blank"
            >
              <Button
                variant={"secondary"}
                ml={2}
                onClick={(e) => {

                }}
              >
                Export as CSV
              </Button>
            </CSVLink>
          )}
        </Flex>
      </Flex>
      {
        isOrgDrafts && (
          <Text mb={4} >All team members with role "Content Writer" or "Admin" can see the team drafts</Text>
        )
      }
      {drafts?.length === 0 ? (
        <Center mt="44">
          <Box textAlign="center">
            <Text>No drafts found</Text>
          </Box>
        </Center>
      ) : (
        <>
          {drafts.map((draft) => (
            <TweetBar
              key={draft.id}
              type="drafts"
              tweet={draft}
              bg={tweetbarBg}
              onDelete={onDelete}
              threadBg={threadBg}
              isOrgDrafts={isOrgDrafts}
            />
          ))}
          {hasMore && (
            <Flex justify="center" mt="16">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => loadMoreDrafts()}
                isLoading={isLoadingMore}
              >
                Load more
              </Button>
              <Button
                size="sm"
                ml={2}
                variant="secondary"
                onClick={() => loadMoreDrafts(true)}
                isLoading={isLoadingMore}
              >
                Load all
              </Button>
            </Flex>
          )}
        </>
      )}
    </Box >
  );
}
